import { useEffect, useState } from 'react';
import type { ImageObject } from '@ox/schema';
import { useEditorStore } from '../store/editor-store';
import {
  deleteOwnerAsset,
  fetchOwnerAssets,
  loadRecentAssets,
  type RecentAsset,
} from '../lib/recentAssets';
import {
  IconAspectLock,
  IconBringForward,
  IconCenterH,
  IconCenterV,
  IconSendBackward,
  IconTrash,
  IconUpload,
} from './Icons';

export function ImagePanel({
  apiBase,
  onUpload,
  onPickRecent,
  onReedit,
  getToken,
}: {
  apiBase: string;
  onUpload: (file: File) => Promise<void>;
  onPickRecent: (assetId: string) => void;
  /** Re-open Editar imagen modal for the selected object */
  onReedit?: () => void;
  getToken?: () => Promise<string | null | undefined>;
}) {
  const selectedId = useEditorStore((s) => s.selectedId);
  const document = useEditorStore((s) => s.document);
  const model = useEditorStore((s) => s.model);
  const activeView = useEditorStore((s) => s.activeView);
  const panel = useEditorStore((s) => s.panel);
  const updateObject = useEditorStore((s) => s.updateObject);
  const centerH = useEditorStore((s) => s.centerH);
  const centerV = useEditorStore((s) => s.centerV);
  const bringForward = useEditorStore((s) => s.bringForward);
  const sendBackward = useEditorStore((s) => s.sendBackward);
  const moveSelectedToView = useEditorStore((s) => s.moveSelectedToView);
  const deleteSelected = useEditorStore((s) => s.deleteSelected);
  const select = useEditorStore((s) => s.select);
  const setPanel = useEditorStore((s) => s.setPanel);
  const lowResWarning = useEditorStore((s) => s.lowResWarning);

  function closePanel() {
    if (panel === 'image' && selectedId) {
      setPanel('design');
      return;
    }
    select(null);
  }
  const [lockRatio, setLockRatio] = useState(true);
  const [recent, setRecent] = useState<RecentAsset[]>([]);
  const [busy, setBusy] = useState(false);
  const [copyrightOpen, setCopyrightOpen] = useState(false);

  const obj = document.views
    .find((v) => v.viewId === activeView)
    ?.objects.find((o) => o.id === selectedId && o.type === 'image') as ImageObject | undefined;

  /** Explicit "Cargar" always shows upload UI, even with a selection. */
  const showUpload = panel === 'image' || !obj;

  async function refreshRecent() {
    const token = getToken ? await getToken() : null;
    const list = await fetchOwnerAssets(apiBase, { token });
    setRecent(list);
  }

  useEffect(() => {
    if (!showUpload) return;
    setRecent(loadRecentAssets());
    void refreshRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUpload, apiBase]);

  if (!showUpload && obj) {
    const area = model?.printAreas[activeView];
    const sizeMin = 10;
    const sizeMax = Math.max(
      80,
      Math.round((area?.width ?? 300) * 1.05),
      Math.ceil(obj.width),
    );
    const clampedW = Math.min(sizeMax, Math.max(sizeMin, Math.round(obj.width)));
    const pct = ((clampedW - sizeMin) / Math.max(1, sizeMax - sizeMin)) * 100;
    const setWidthMm = (w: number) => {
      const nextW = Math.min(sizeMax, Math.max(sizeMin, w));
      const nextH = lockRatio
        ? Math.max(sizeMin, obj.height * (nextW / Math.max(obj.width, 0.01)))
        : obj.height;
      updateObject(obj.id, { width: nextW, height: nextH });
    };
    return (
      <div className="ox-side-panel">
        <div className="ox-panel-head">
          <h3>Editar diseño</h3>
          <button type="button" className="ox-panel-close" onClick={closePanel} aria-label="Cerrar">
            ✕
          </button>
        </div>
        {lowResWarning && (
          <div className="ox-warn">La imagen tiene baja resolución para imprimir bien.</div>
        )}
        <div className="ox-action-icon-grid">
          <button type="button" onClick={centerH} title="Centrar horizontal">
            <IconCenterH />
            <span>Centrar horizontal</span>
          </button>
          <button type="button" onClick={centerV} title="Centrar vertical">
            <IconCenterV />
            <span>Centrar vertical</span>
          </button>
          <button type="button" onClick={bringForward} title="Mover adelante">
            <IconBringForward />
            <span>Mover adelante</span>
          </button>
          <button type="button" onClick={sendBackward} title="Mover atrás">
            <IconSendBackward />
            <span>Mover atrás</span>
          </button>
          {(model?.views.length ?? 0) > 1 ? (
            <button
              type="button"
              onClick={() => {
                const other =
                  model!.views.find((v) => v.id !== activeView)?.id ??
                  (activeView === 'front' ? 'back' : 'front');
                moveSelectedToView(other);
              }}
              title="Mover a otra vista"
            >
              <IconSendBackward />
              <span>Otra vista</span>
            </button>
          ) : null}
          <button type="button" className="danger" onClick={deleteSelected} title="Borrar">
            <IconTrash />
            <span>Borrar</span>
          </button>
        </div>

        <div className="ox-size-scale">
          <div className="ox-size-scale-head">
            <span>Escalar</span>
            <span className="ox-size-scale-readout">
              {Math.round(obj.width)} × {Math.round(obj.height)} mm
            </span>
          </div>
          <div className="ox-m-size-slider ox-size-scale-slider">
            <input
              type="range"
              min={sizeMin}
              max={sizeMax}
              step={1}
              value={clampedW}
              style={{
                background: `linear-gradient(to right, var(--ox-accent, #e07a6a) ${pct}%, #e5e5e5 ${pct}%)`,
              }}
              onChange={(e) => setWidthMm(Number(e.target.value) || sizeMin)}
              aria-label="Escalar diseño"
            />
            <input
              type="number"
              min={sizeMin}
              max={sizeMax}
              value={Math.round(obj.width)}
              onChange={(e) => setWidthMm(Number(e.target.value) || sizeMin)}
              aria-label="Ancho en mm"
              title="Ancho (mm)"
            />
          </div>
          <button
            type="button"
            className={`ox-lock-ratio ox-lock-ratio--labeled ${lockRatio ? 'active' : ''}`}
            onClick={() => setLockRatio((v) => !v)}
            title={lockRatio ? 'Proporción bloqueada' : 'Proporción libre'}
            aria-pressed={lockRatio}
          >
            <IconAspectLock size={16} />
            {lockRatio ? 'Proporción bloqueada' : 'Proporción libre'}
          </button>
        </div>

        {onReedit && !obj.galleryCode && (
          <button type="button" className="ox-secondary-btn ox-m-full-btn" onClick={onReedit}>
            Editar imagen (forma / fondo)
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="ox-side-panel">
      <div className="ox-panel-head">
        <h3>Cargar tu imagen</h3>
        <button type="button" className="ox-panel-close" onClick={closePanel} aria-label="Cerrar">
          ✕
        </button>
      </div>

      <div className="ox-upload-dropzone">
        <p className="ox-upload-dropzone-lead">Arrastra tu imagen aquí</p>
        <p className="ox-upload-dropzone-or">o</p>
        <input
          id="ox-file-input"
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (!f) return;
            setBusy(true);
            void onUpload(f)
              .then(() => refreshRecent())
              .finally(() => setBusy(false));
          }}
        />
        <button
          type="button"
          className="ox-primary ox-m-full-btn ox-upload-cta"
          disabled={busy}
          onClick={() => setCopyrightOpen(true)}
        >
          <IconUpload size={18} /> {busy ? 'Subiendo…' : 'Cargar imagen'}
        </button>
        <p className="ox-hint ox-upload-dropzone-legal">
          Al cargar una imagen aceptas las condiciones de uso y copyright.
        </p>
      </div>
      <p className="ox-hint">PNG, JPG, HEIC. Max. 15MB. Resolución mínima recomendada 150 ppp.</p>

      <h4 className="ox-m-section-title">Tus imágenes recientes</h4>
      <div className="ox-m-recent-grid ox-desktop-recent">
        {recent.length === 0 && <p className="ox-hint">Aún no hay imágenes recientes.</p>}
        {recent.map((r) => (
          <div key={r.assetId} className="ox-m-recent-item">
            <button type="button" className="ox-m-recent-thumb" onClick={() => onPickRecent(r.assetId)}>
              <img src={r.thumbUrl} alt="" />
            </button>
            <button
              type="button"
              className="ox-m-recent-del"
              aria-label="Eliminar"
              onClick={() => {
                void (async () => {
                  const token = getToken ? await getToken() : null;
                  setRecent((prev) => prev.filter((x) => x.assetId !== r.assetId));
                  await deleteOwnerAsset(apiBase, r.assetId, { token });
                  setRecent(loadRecentAssets());
                })();
              }}
            >
              🗑
            </button>
          </div>
        ))}
      </div>

      {busy && (
        <div className="ox-upload-busy ox-upload-busy--panel" role="status" aria-live="polite">
          <div className="ox-upload-busy-card">
            <div className="ox-upload-spinner" aria-hidden />
            <p>Subiendo imagen…</p>
          </div>
        </div>
      )}
      {copyrightOpen && (
        <div className="ox-modal-backdrop" onClick={() => setCopyrightOpen(false)}>
          <div className="ox-modal ox-copyright-modal" onClick={(e) => e.stopPropagation()}>
            <header>
              <h2>Condiciones de uso</h2>
              <button type="button" onClick={() => setCopyrightOpen(false)} aria-label="Cerrar">
                ✕
              </button>
            </header>
            <p>
              Al cargar una imagen confirmas que tienes los derechos de uso y copyright, y aceptas
              las condiciones de uso del servicio.
            </p>
            <div className="ox-copyright-actions">
              <button type="button" className="ox-secondary-btn" onClick={() => setCopyrightOpen(false)}>
                Cancelar
              </button>
              <button
                type="button"
                className="ox-primary"
                onClick={() => {
                  setCopyrightOpen(false);
                  window.setTimeout(
                    () => window.document.getElementById('ox-file-input')?.click(),
                    50,
                  );
                }}
              >
                Acepto y continuar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
